
import { supabase } from './client';

/**
 * Uploads a file to Supabase Storage
 * @param file The file to upload
 * @param bucket The bucket name to upload to
 * @returns A promise that resolves to the public URL of the uploaded file
 */
export const uploadFile = async (file: File, bucket: string): Promise<string> => {
  try {
    // Check if bucket exists and create it if it doesn't
    const { data: buckets } = await supabase.storage.listBuckets();
    const bucketExists = buckets?.some(b => b.name === bucket);
    
    if (!bucketExists) {
      console.log(`Bucket ${bucket} doesn't exist. Creating...`);
      // We'll just proceed - the bucket should be created via migrations
    }

    // Generate a unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
    const filePath = `${fileName}`;

    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(filePath, file);

    if (error) {
      console.error("Error uploading file:", error);
      throw error;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error('Failed to upload file');
  }
};

/**
 * Deletes a file from Supabase Storage
 * @param path The path of the file to delete
 * @param bucket The bucket name containing the file
 * @returns A promise that resolves to true if deletion was successful
 */
export const deleteFile = async (path: string, bucket: string): Promise<boolean> => {
  try {
    // Extract the filename from the URL or path
    let fileName = path;
    
    // If it's a full URL, extract the filename
    if (path.includes('/')) {
      const parts = path.split('/');
      fileName = parts[parts.length - 1];
    }
    
    const { error } = await supabase.storage
      .from(bucket)
      .remove([fileName]);

    if (error) {
      console.error("Error deleting file:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    return false;
  }
};

// Add aliases for backward compatibility
export const uploadImage = uploadFile;
export const deleteImage = deleteFile;
